import { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, Newspaper, Map, Table2, Calendar, MapPin, Building2, Radio, Users, Landmark, Radar, Bell, TerminalSquare, RefreshCw, Moon, Sun, Search, Settings as Gear, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { InstallButton } from "@/components/InstallApp";
import { toast } from "sonner";
import { get, post } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const NAV = [
  ["/", "Tableau de bord", LayoutDashboard, "dashboard"], ["/recap", "Récap du jour", Newspaper, "recap"], ["/evenements", "Événements", Table2, "events"],
  ["/calendrier", "Calendrier", Calendar, "calendar"], ["/carte-territoire", "Carte", Map, "territory-map"], ["/carte", "Carte des événements", MapPin, "map"],
  ["/communes", "Communes & territoires", Building2, "communes"], ["/sources", "Sources", Radio, "sources"],
  ["/organisateurs", "Organisateurs", Users, "organizers"], ["/lieux", "Lieux", Landmark, "venues"],
  ["/signaux", "Signaux faibles", Radar, "signals"], ["/alertes", "Alertes", Bell, "alerts"],
  ["/journal", "Journal technique", TerminalSquare, "techlog"],
];

const DeltaChip = () => {
  const { data } = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/dashboard"), refetchInterval: 120000 });
  if (!data) return null;
  return (
    <NavLink to={`/evenements?is_new=1&since=${encodeURIComponent(data.delta.since)}`} className="hidden xl:flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300" data-testid="header-delta">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
      <span><b className="font-mono">{data.delta.new}</b> nouveaux · <b className="font-mono">{data.delta.modified}</b> modifications depuis votre dernière consultation</span>
    </NavLink>
  );
};

export default function Layout() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(false);
  const search = (e) => { e.preventDefault(); setMenu(false); nav(`/evenements?q=${encodeURIComponent(q)}`); };
  const [dark, setDark] = useState(() => localStorage.getItem("theme") === "dark");
  const { data: notif } = useQuery({ queryKey: ["notif"], queryFn: () => get("/alerts/notifications", { unread: true }), refetchInterval: 60000 });

  useEffect(() => { post("/visit").then(() => qc.invalidateQueries({ queryKey: ["dashboard"] })); }, [qc]);
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);

  const collect = async () => {
    const r = await post("/collect/run");
    toast(r.started ? "Collecte lancée — les résultats apparaîtront progressivement" : r.message);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="app-header sticky top-0 z-40 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 flex items-center gap-2 sm:gap-4 sm:px-4 lg:px-6">
        <Button data-testid="mobile-menu-btn" size="sm" variant="ghost" className="h-8 px-2 md:hidden" onClick={() => setMenu(true)} aria-label="Menu"><Menu className="h-5 w-5" /></Button>
        <NavLink to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
          <div className="h-7 w-7 rounded-[4px] bg-slate-900 dark:bg-slate-100 grid place-items-center">
            <Radar className="h-4 w-4 text-white dark:text-slate-900" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold tracking-tight text-[15px]">Radar Territorial</div>
            <div className="overline !text-[9px] !tracking-[0.2em] hidden sm:block">veille événementielle</div>
          </div>
        </NavLink>
        <form className="relative flex-1 max-w-md hidden md:block" onSubmit={search}>
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
          <Input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un événement, un lieu, un organisateur…" className="h-8 pl-8 text-xs" />
        </form>
        <DeltaChip />
        <div className="ml-auto flex items-center gap-1.5">
          <Button data-testid="manual-collect-btn" size="sm" variant="outline" className="h-8 text-xs px-2 sm:px-3" onClick={collect} title="Collecter">
            <RefreshCw className="h-3.5 w-3.5 sm:mr-1.5" /><span className="hidden sm:inline">Collecter</span>
          </Button>
          <Button data-testid="notifications-btn" size="sm" variant="ghost" className="h-8 relative" onClick={() => nav("/alertes")}>
            <Bell className="h-4 w-4" />
            {notif?.unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-rose-600 text-white text-[9px] font-mono grid place-items-center">{notif.unread}</span>}
          </Button>
          <Button data-testid="theme-toggle" size="sm" variant="ghost" className="h-8" onClick={() => setDark(!dark)}>
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button data-testid="settings-btn" size="sm" variant="ghost" className="h-8" title="Paramètres" onClick={() => nav("/parametres")}>
            <Gear className="h-4 w-4" />
          </Button>
        </div>
      </header>
      <nav className="hidden md:flex bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-4 lg:px-6 items-center gap-0.5 overflow-x-auto">
        {NAV.map(([to, label, Icon, id]) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${id}`}
            className={({ isActive }) => `flex items-center gap-1.5 px-2.5 py-2.5 text-xs whitespace-nowrap border-b-2 transition-colors duration-150 ${isActive ? "border-slate-900 dark:border-slate-100 text-slate-900 dark:text-white font-semibold" : "border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-slate-200"}`}>
            <Icon className="h-3.5 w-3.5" />{label}
          </NavLink>
        ))}
      </nav>
      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" aria-describedby={undefined} className="w-[82vw] max-w-xs p-0 overflow-y-auto safe-top safe-bottom" data-testid="mobile-menu">
          <SheetTitle className="px-4 pt-4 pb-2 font-display text-base">Radar Territorial</SheetTitle>
          <form className="relative px-4 pb-3" onSubmit={search}>
            <Search className="absolute top-2.5 h-3.5 w-3.5 text-slate-400" style={{ left: 26 }} />
            <Input data-testid="mobile-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="h-9 pl-8 text-sm" />
          </form>
          <div className="px-4 pb-3"><InstallButton className="w-full" onDone={() => setMenu(false)} /></div>
          <nav className="flex flex-col pb-4">
            {NAV.map(([to, label, Icon, id]) => (
              <NavLink key={to} to={to} end={to === "/"} data-testid={`mnav-${id}`} onClick={() => setMenu(false)}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 ${isActive ? "border-slate-900 dark:border-slate-100 bg-slate-50 dark:bg-slate-800 font-semibold" : "border-transparent text-slate-600 dark:text-slate-300"}`}>
                <Icon className="h-4 w-4" />{label}
              </NavLink>
            ))}
          </nav>
        </SheetContent>
      </Sheet>
      <main className="flex-1 min-h-0">
        <Outlet />
      </main>
    </div>
  );
}
